import { generarAsientoBajaActivo, generarAsientoDepreciacion } from "../core/accounting.js";
import { getParametrosFiscales, saveData, siguienteNumeroDoc } from "../core/cloudSync.js";
import { C, CATEGORIAS_ACTIVO_FIJO, MEDIOS_PAGO, MOTIVOS_BAJA_ACTIVO } from "../core/config.js";
import { calcularBajaActivo, esActivoMenorCuantia, liquidarDepreciacionActivo } from "../core/depreciacion.js";
import { state } from "../core/state.js";
import { btnStyle, escapeHTML, fmtCOP, fmtDate, genId, inputStyle, parseNumber, today } from "../core/utils.js";
import { render } from "../ui/app.js";

function kardexSinBajas(activo) {
  return (activo.depreciaciones || []).filter(k => k.origen !== "baja");
}

function getDepreciacionAcumulada(activo) {
  const k = kardexSinBajas(activo);
  return k.length === 0 ? 0 : (k[k.length - 1].depreciacionAcumuladaContable || 0);
}

function getValorEnLibros(activo) {
  const k = kardexSinBajas(activo);
  if (k.length === 0) return parseNumber(activo.costo_adquisicion) || 0;
  return k[k.length - 1].valorEnLibros;
}

function getMesesRestantes(activo) {
  const vidaUtil = parseNumber(activo.vida_util_contable_meses) || 0;
  if (vidaUtil <= 0) return "—";
  const mesesCorridos = kardexSinBajas(activo).filter(k => k.origen === "mensual").length;
  return Math.max(0, vidaUtil - mesesCorridos);
}

function categoriaInfo(categoria) {
  return CATEGORIAS_ACTIVO_FIJO.find(c => c.value === categoria) || {};
}

function primerYUltimoDiaMes(mesInput) {
  // mesInput viene de <input type="month"> con formato "YYYY-MM"
  const [y, m] = (mesInput || "").split("-").map(Number);
  if (!y || !m) return null;
  const desde = `${y}-${String(m).padStart(2, "0")}-01`;
  const ultimo = new Date(y, m, 0).getDate();
  const hasta = `${y}-${String(m).padStart(2, "0")}-${String(ultimo).padStart(2, "0")}`;
  return { desde, hasta };
}

export function renderActivosFijos() {
  if (!state.afTab) state.afTab = "activos";
  const tab = state.afTab;

  const header = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;padding-bottom:10px;border-bottom:3px solid ${C.gold};">
      <div style="display:flex;gap:10px;align-items:center;">
        <img src="${window.LOGO}" alt="Aura" style="height:42px;object-fit:contain;"/>
        <div>
          <h2 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">🏢 Activos Fijos</h2>
          <p style="margin:3px 0 0;color:${C.muted};font-size:11.5px;">Control de propiedad, planta y equipo, depreciación en línea recta y bajas de activos</p>
        </div>
      </div>
    </div>
  `;

  const tabs = [
    { id: "activos", label: "📋 Activos" },
    { id: "depreciacion", label: "🧮 Depreciación" },
    { id: "bajas", label: "🗑️ Bajas" },
    { id: "historico", label: "📜 Histórico" }
  ].map(t => `
    <button data-action="af-tab" data-tab="${t.id}" style="
      padding:10px 18px;background:${tab === t.id ? "white" : "transparent"};
      border:none;border-bottom:3px solid ${tab === t.id ? C.accent : "transparent"};
      color:${tab === t.id ? "#0E7490" : C.muted};font-weight:700;font-size:12px;cursor:pointer;margin-bottom:-1px;font-family:inherit;
    ">${t.label}</button>
  `).join("");

  let body;
  if (tab === "depreciacion") body = renderDepreciacionTab();
  else if (tab === "bajas") body = renderBajasTab();
  else if (tab === "historico") body = renderHistoricoTab();
  else body = renderActivosTab();

  return `
    ${header}
    <div style="display:flex;border-bottom:1px solid ${C.border};margin-bottom:10px;overflow-x:auto;">${tabs}</div>
    ${body}
  `;
}

// ─── Tab: Activos (listado + alta) ─────────────────────────────────────────

function renderActivosTab() {
  const activos = [...(state.data.activos_fijos || [])].sort((a, b) => (a.codigo || "").localeCompare(b.codigo || ""));

  const filas = activos.map(a => {
    const cat = categoriaInfo(a.categoria);
    const acumulada = getDepreciacionAcumulada(a);
    const valorLibros = getValorEnLibros(a);
    const dadaDeBaja = a.estado === "dado_de_baja";
    return `
      <tr style="border-top:1px solid ${C.border};${dadaDeBaja ? "opacity:.55;" : ""}">
        <td style="padding:6px 9px;font-weight:700;">${escapeHTML(a.codigo || "—")}</td>
        <td style="padding:6px 9px;">${escapeHTML(a.nombre || "—")}</td>
        <td style="padding:6px 9px;font-size:10.5px;color:${C.muted};">${escapeHTML(cat.label || a.categoria || "—")}</td>
        <td style="padding:6px 9px;text-align:right;">${fmtCOP(parseNumber(a.costo_adquisicion) || 0)}</td>
        <td style="padding:6px 9px;text-align:right;color:#0E7490;">${fmtCOP(acumulada)}</td>
        <td style="padding:6px 9px;text-align:right;font-weight:700;">${fmtCOP(valorLibros)}</td>
        <td style="padding:6px 9px;text-align:center;font-size:10.5px;color:${C.muted};">${getMesesRestantes(a)}</td>
        <td style="padding:6px 9px;text-align:center;">${dadaDeBaja
          ? `<span style="color:#dc2626;font-weight:700;font-size:10.5px;">Dado de baja</span>`
          : `<span style="color:#059669;font-weight:700;font-size:10.5px;">Activo</span>`}</td>
      </tr>
    `;
  }).join("");

  const tabla = `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="background:#f1f5f9;">
            <th style="padding:6px 9px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">CÓDIGO</th>
            <th style="padding:6px 9px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">NOMBRE</th>
            <th style="padding:6px 9px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">CATEGORÍA</th>
            <th style="padding:6px 9px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">COSTO</th>
            <th style="padding:6px 9px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">DEPREC. ACUM.</th>
            <th style="padding:6px 9px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">VALOR EN LIBROS</th>
            <th style="padding:6px 9px;text-align:center;font-size:10.5px;font-weight:700;color:${C.text};">MESES REST.</th>
            <th style="padding:6px 9px;text-align:center;font-size:10.5px;font-weight:700;color:${C.text};">ESTADO</th>
          </tr>
        </thead>
        <tbody>${filas.length === 0
          ? `<tr><td colspan="8" style="padding:30px;text-align:center;color:${C.muted};">Sin activos fijos registrados aún.</td></tr>`
          : filas}</tbody>
      </table>
    </div>
  `;

  const boton = `<div style="margin-bottom:9px;"><button data-action="af-nuevo-activo" style="${btnStyle("#0E7490")}">+ Nuevo activo</button></div>`;

  return state.afNuevoActivo ? renderFormNuevoActivo() : (boton + tabla);
}

function renderFormNuevoActivo() {
  const a = state.afNuevoActivo;
  const cat = categoriaInfo(a.categoria);
  const uvt = getParametrosFiscales().uvt;
  const costo = parseNumber(a.costo_adquisicion) || 0;
  const menorCuantia = costo > 0 && esActivoMenorCuantia(costo, uvt);

  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;">
      <h3 style="margin:0 0 9px;font-size:12px;color:${C.text};">Nuevo activo fijo</h3>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;">
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Nombre</label>
          <input id="af-nombre" data-action="af-campo-input" data-campo="nombre" type="text" value="${escapeHTML(a.nombre || "")}" style="${inputStyle}"/>
        </div>
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Categoría</label>
          <select id="af-categoria" data-action="af-categoria-change" style="${inputStyle}">
            <option value="">— Seleccionar —</option>
            ${CATEGORIAS_ACTIVO_FIJO.map(c => `<option value="${c.value}" ${a.categoria === c.value ? "selected" : ""}>${c.label}</option>`).join("")}
          </select>
        </div>
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Fecha de compra</label>
          <input id="af-fecha-compra" data-action="af-campo-input" data-campo="fecha_compra" type="date" value="${a.fecha_compra || today()}" style="${inputStyle}"/>
        </div>
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Costo de adquisición</label>
          <input id="af-costo" data-action="af-costo-change" type="number" min="0" step="1" value="${a.costo_adquisicion || ""}" style="${inputStyle}text-align:right;"/>
        </div>
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Valor residual</label>
          <input id="af-valor-residual" data-action="af-campo-input" data-campo="valor_residual" type="number" min="0" step="1" value="${a.valor_residual || 0}" style="${inputStyle}text-align:right;"/>
        </div>
        <div></div>
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Vida útil contable (meses)</label>
          <input id="af-vida-contable" data-action="af-campo-input" data-campo="vida_util_contable_meses" type="number" min="0" step="1" value="${a.vida_util_contable_meses ?? (cat.vidaUtilFiscalMeses || "")}" style="${inputStyle}text-align:right;"/>
          <div style="font-size:10px;color:${C.muted};margin-top:3px;">Según la vida útil económica real del activo (NIIF para Pymes, Sección 17).</div>
        </div>
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Vida útil fiscal (meses)</label>
          <input id="af-vida-fiscal" data-action="af-campo-input" data-campo="vida_util_fiscal_meses" type="number" min="0" step="1" value="${a.vida_util_fiscal_meses ?? (cat.vidaUtilFiscalMeses || "")}" style="${inputStyle}text-align:right;"/>
          <div style="font-size:10px;color:${C.muted};margin-top:3px;">Sugerida según tasas máximas Art. 137 E.T. — editable, debe soportarse técnicamente.</div>
        </div>
        <div style="display:flex;align-items:center;">
          <label style="display:flex;align-items:center;gap:8px;font-size:11.5px;color:${C.text};cursor:pointer;margin-top:18px;">
            <input id="af-turnos" data-action="af-campo-input" data-campo="turnos_adicionales" type="checkbox" ${a.turnos_adicionales ? "checked" : ""}/>
            Turnos adicionales (+25% fiscal, no aplica a inmuebles)
          </label>
        </div>
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Responsable</label>
          <input id="af-responsable" data-action="af-campo-input" data-campo="responsable" type="text" value="${escapeHTML(a.responsable || "")}" style="${inputStyle}"/>
        </div>
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Ubicación</label>
          <input id="af-ubicacion" data-action="af-campo-input" data-campo="ubicacion" type="text" value="${escapeHTML(a.ubicacion || "")}" style="${inputStyle}"/>
        </div>
      </div>

      ${menorCuantia ? `
        <div style="margin-top:16px;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:7px 10px;font-size:11.5px;color:#92400e;">
          ⚠️ Este activo tiene un costo de adquisición ≤ 50 UVT ($${fmtCOP(50 * uvt)}) y puede depreciarse en su totalidad en el año de compra (Art. 1.2.1.18.5, Decreto 1625 de 2016 — "activos de menor cuantía").
          <label style="display:flex;align-items:center;gap:6px;margin-top:8px;cursor:pointer;font-weight:700;">
            <input id="af-menor-cuantia" data-action="af-campo-input" data-campo="depreciar_menor_cuantia" type="checkbox" ${a.depreciar_menor_cuantia ? "checked" : ""}/>
            Depreciar 100% en el año de compra
          </label>
        </div>
      ` : ""}

      <div style="display:flex;gap:10px;margin-top:20px;padding-top:16px;border-top:1px solid ${C.border};">
        <button data-action="af-cancelar-nuevo" style="${btnStyle(null, "secondary")}">Cancelar</button>
        <button data-action="af-guardar-activo" style="${btnStyle("#0E7490")}">💾 Guardar activo</button>
      </div>
    </div>
  `;
}

// ─── Tab: Depreciación (correr un período) ─────────────────────────────────

function renderDepreciacionTab() {
  const liq = state.afLiquidacionActual;
  if (!liq) return renderFormCorrerDepreciacion();
  return renderDetalleDepreciacion(liq);
}

function renderFormCorrerDepreciacion() {
  const hoy = new Date(today() + "T12:00:00");
  const mesActual = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`;
  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;">
      <h3 style="margin:0 0 4px;font-size:12px;color:${C.text};">Correr depreciación del período</h3>
      <p style="margin:0 0 9px;font-size:11.5px;color:${C.muted};">Se calculará la depreciación mensual (línea recta) de todos los activos vigentes que aún no estén totalmente depreciados en el mes seleccionado.</p>
      <div style="display:flex;gap:10px;align-items:flex-end;">
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Mes a depreciar</label>
          <input id="af-mes-depreciar" type="month" value="${mesActual}" style="${inputStyle}"/>
        </div>
        <button data-action="af-calcular-depreciacion" style="${btnStyle("#0E7490")}">🧮 Calcular depreciación</button>
      </div>
    </div>
  `;
}

function renderDetalleDepreciacion(liq) {
  const detalle = liq.detalle_activos || [];
  const total = detalle.reduce((s, d) => s + d.depreciacionContable, 0);

  const rows = detalle.map(d => `
    <tr style="border-top:1px solid ${C.border};">
      <td style="padding:6px 9px;font-weight:700;">${escapeHTML(d.activoNombre)}</td>
      <td style="padding:6px 9px;text-align:right;">${fmtCOP(d.depreciacionContable)}</td>
      <td style="padding:6px 9px;text-align:right;font-size:10.5px;color:${C.muted};">${fmtCOP(d.depreciacionFiscal)}</td>
      <td style="padding:6px 9px;text-align:right;">${fmtCOP(d.valorEnLibros)}</td>
      <td style="padding:6px 9px;text-align:center;font-size:10.5px;color:${C.muted};">${d.origen === "menor_cuantia" ? "Menor cuantía (100%)" : "Mensual"}</td>
    </tr>
  `).join("");

  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px;">
        <div>
          <h3 style="margin:0;font-size:12px;color:${C.text};">Depreciación calculada — ${fmtDate(liq.periodo_desde)} → ${fmtDate(liq.periodo_hasta)}</h3>
          <p style="margin:3px 0 0;font-size:11.5px;color:${C.muted};">${detalle.length} activo${detalle.length !== 1 ? "s" : ""} · Total a depreciar: <strong style="color:#0E7490;">${fmtCOP(total)}</strong></p>
        </div>
        <div style="display:flex;gap:10px;">
          <button data-action="af-descartar-depreciacion" style="${btnStyle(null, "secondary")}">✕ Descartar</button>
          <button data-action="af-confirmar-depreciacion" style="${btnStyle("#0E7490")}" ${detalle.length === 0 ? "disabled" : ""}>✅ Confirmar y generar asiento</button>
        </div>
      </div>
    </div>
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="background:#f1f5f9;">
            <th style="padding:6px 9px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">ACTIVO</th>
            <th style="padding:6px 9px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">DEPREC. CONTABLE</th>
            <th style="padding:6px 9px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">DEPREC. FISCAL</th>
            <th style="padding:6px 9px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">VALOR EN LIBROS</th>
            <th style="padding:6px 9px;text-align:center;font-size:10.5px;font-weight:700;color:${C.text};">TIPO</th>
          </tr>
        </thead>
        <tbody>${detalle.length === 0
          ? `<tr><td colspan="5" style="padding:30px;text-align:center;color:${C.muted};">No hay activos pendientes de depreciar en este período (o ya se corrió este mes).</td></tr>`
          : rows}</tbody>
      </table>
    </div>
  `;
}

// ─── Tab: Bajas de activos ──────────────────────────────────────────────────

function renderBajasTab() {
  if (state.afBajaActivoId) return renderFormBaja(state.afBajaActivoId);

  const activos = (state.data.activos_fijos || []).filter(a => a.estado !== "dado_de_baja");
  const filas = activos.map(a => `
    <tr style="border-top:1px solid ${C.border};">
      <td style="padding:6px 9px;font-weight:700;">${escapeHTML(a.codigo || "—")}</td>
      <td style="padding:6px 9px;">${escapeHTML(a.nombre || "—")}</td>
      <td style="padding:6px 9px;text-align:right;">${fmtCOP(getValorEnLibros(a))}</td>
      <td style="padding:6px 9px;text-align:right;">
        <button data-action="af-seleccionar-baja" data-id="${a.id}" style="${btnStyle(null, "ghost", true)}">🗑️ Dar de baja</button>
      </td>
    </tr>
  `).join("");

  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="background:#f1f5f9;">
            <th style="padding:6px 9px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">CÓDIGO</th>
            <th style="padding:6px 9px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">NOMBRE</th>
            <th style="padding:6px 9px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">VALOR EN LIBROS</th>
            <th></th>
          </tr>
        </thead>
        <tbody>${filas.length === 0
          ? `<tr><td colspan="4" style="padding:30px;text-align:center;color:${C.muted};">No hay activos vigentes para dar de baja.</td></tr>`
          : filas}</tbody>
      </table>
    </div>
  `;
}

function renderFormBaja(activoId) {
  const a = (state.data.activos_fijos || []).find(x => x.id === activoId);
  if (!a) return `<div style="color:${C.muted};">Activo no encontrado.</div>`;
  const valorLibros = getValorEnLibros(a);

  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;">
      <h3 style="margin:0 0 4px;font-size:12px;color:${C.text};">Dar de baja: ${escapeHTML(a.nombre)}</h3>
      <p style="margin:0 0 9px;font-size:11.5px;color:${C.muted};">Valor en libros actual: <strong>${fmtCOP(valorLibros)}</strong></p>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;">
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Fecha de baja</label>
          <input id="af-fecha-baja" type="date" value="${today()}" style="${inputStyle}"/>
        </div>
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Motivo</label>
          <select id="af-motivo-baja" style="${inputStyle}">
            ${MOTIVOS_BAJA_ACTIVO.map(m => `<option value="${m.value}">${m.label}</option>`).join("")}
          </select>
        </div>
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Valor de venta (0 si no hay venta)</label>
          <input id="af-valor-venta" type="number" min="0" step="1" value="0" style="${inputStyle}text-align:right;"/>
        </div>
        <div>
          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Medio de pago recibido</label>
          <select id="af-medio-pago-baja" style="${inputStyle}">
            ${MEDIOS_PAGO.map(m => `<option value="${m.value}">${m.label}</option>`).join("")}
          </select>
        </div>
      </div>
      <div style="display:flex;gap:10px;margin-top:20px;padding-top:16px;border-top:1px solid ${C.border};">
        <button data-action="af-cancelar-baja" style="${btnStyle(null, "secondary")}">Cancelar</button>
        <button data-action="af-confirmar-baja" data-id="${a.id}" style="${btnStyle("#dc2626")}">✅ Confirmar baja</button>
      </div>
    </div>
  `;
}

// ─── Tab: Histórico ──────────────────────────────────────────────────────────

function renderHistoricoTab() {
  const eventos = [];
  (state.data.activos_fijos || []).forEach(a => {
    (a.depreciaciones || []).forEach(k => {
      eventos.push({ ...k, activoNombre: a.nombre, activoCodigo: a.codigo });
    });
  });
  eventos.sort((x, y) => (y.fecha || "").localeCompare(x.fecha || ""));

  if (eventos.length === 0) {
    return `
      <div style="padding:26px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;">
        <div style="font-size:40px;margin-bottom:10px;">📜</div>
        <div style="color:${C.muted};font-size:14px;">Sin movimientos de depreciación o bajas registrados aún.</div>
      </div>
    `;
  }

  const rows = eventos.map(e => {
    const esBaja = e.origen === "baja";
    const tipoLabel = esBaja ? "Baja de activo" : (e.origen === "menor_cuantia" ? "Menor cuantía (100%)" : "Depreciación mensual");
    const monto = esBaja ? e.utilidadPerdida : e.depreciacionContable;
    return `
      <tr style="border-top:1px solid ${C.border};">
        <td style="padding:6px 9px;">${fmtDate(e.fecha)}</td>
        <td style="padding:6px 9px;font-weight:700;">${escapeHTML(e.activoCodigo || "—")} · ${escapeHTML(e.activoNombre || "—")}</td>
        <td style="padding:6px 9px;font-size:10.5px;color:${C.muted};">${tipoLabel}</td>
        <td style="padding:6px 9px;text-align:right;font-weight:700;color:${esBaja && monto < 0 ? "#dc2626" : "#0E7490"};">${fmtCOP(monto)}</td>
        <td style="padding:6px 9px;">${escapeHTML(e.numero || "—")}</td>
      </tr>
    `;
  }).join("");

  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="background:#f1f5f9;">
            <th style="padding:6px 9px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">FECHA</th>
            <th style="padding:6px 9px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">ACTIVO</th>
            <th style="padding:6px 9px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">TIPO</th>
            <th style="padding:6px 9px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">MONTO</th>
            <th style="padding:6px 9px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">N° ASIENTO</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `;
}

// ─── Acciones ────────────────────────────────────────────────────────────────

import { registerActions } from "../ui/actions.js";

registerActions({
  "af-tab": (d) => {
    state.afTab = d.tab;
    render();
  },
  "af-nuevo-activo": () => {
    state.afNuevoActivo = { turnos_adicionales: false, depreciar_menor_cuantia: false };
    render();
  },
  "af-cancelar-nuevo": () => {
    state.afNuevoActivo = null;
    render();
  },
  "af-campo-input": (d, el) => {
    state.afNuevoActivo[d.campo] = el.type === "checkbox" ? el.checked : el.value;
  },
  "af-categoria-change": (d, el) => {
    const cat = categoriaInfo(el.value);
    state.afNuevoActivo.categoria = el.value;
    if (cat.vidaUtilFiscalMeses != null) {
      state.afNuevoActivo.vida_util_contable_meses = cat.vidaUtilFiscalMeses;
      state.afNuevoActivo.vida_util_fiscal_meses = cat.vidaUtilFiscalMeses;
    }
    render();
  },
  "af-costo-change": (d, el) => {
    state.afNuevoActivo.costo_adquisicion = parseFloat(el.value) || 0;
    render();
  },
  "af-guardar-activo": () => {
    const a = state.afNuevoActivo;
    const nombre = (a.nombre || "").trim();
    if (!a.categoria) { alert("⚠️ Selecciona una categoría."); return; }
    if (!nombre) { alert("⚠️ Ingresa un nombre para el activo."); return; }
    const cat = categoriaInfo(a.categoria);
    const costo = parseNumber(a.costo_adquisicion) || 0;
    if (costo <= 0) { alert("⚠️ Ingresa un costo de adquisición mayor a cero."); return; }

    const activos = state.data.activos_fijos || [];
    const consecutivo = activos.length + 1;
    const nuevo = {
      id: genId(),
      codigo: "AF-" + String(consecutivo).padStart(4, "0"),
      nombre,
      categoria: a.categoria,
      cuenta_activo: cat.cuentaActivo || null,
      fecha_compra: a.fecha_compra || today(),
      costo_adquisicion: costo,
      valor_residual: parseNumber(a.valor_residual) || 0,
      vida_util_contable_meses: parseNumber(a.vida_util_contable_meses) || 0,
      vida_util_fiscal_meses: parseNumber(a.vida_util_fiscal_meses) || null,
      metodo: "linea_recta",
      turnos_adicionales: !!a.turnos_adicionales,
      depreciar_menor_cuantia: !!a.depreciar_menor_cuantia,
      responsable: a.responsable || "",
      ubicacion: a.ubicacion || "",
      estado: "activo",
      fecha_baja: null, motivo_baja: null, valor_venta: null,
      depreciaciones: []
    };
    saveData("activos_fijos", [...activos, nuevo]);
    state.afNuevoActivo = null;
    alert(`✅ Activo ${nuevo.codigo} guardado.`);
    render();
  },
  "af-calcular-depreciacion": () => {
    const mes = document.getElementById("af-mes-depreciar")?.value;
    const rango = primerYUltimoDiaMes(mes);
    if (!rango) { alert("⚠️ Selecciona un mes válido."); return; }
    const uvt = getParametrosFiscales().uvt;

    const activos = (state.data.activos_fijos || []).filter(a => a.estado !== "dado_de_baja");
    const detalle_activos = [];
    activos.forEach(a => {
      const yaCorrido = (a.depreciaciones || []).some(k => k.fecha === rango.hasta && k.origen !== "baja");
      if (yaCorrido) return;
      const record = liquidarDepreciacionActivo(a, rango.hasta, uvt);
      if (record.depreciacionContable > 0) {
        detalle_activos.push({ activoId: a.id, activoNombre: a.nombre, ...record });
      }
    });

    state.afLiquidacionActual = {
      id: genId(), periodo_desde: rango.desde, periodo_hasta: rango.hasta, fecha: rango.hasta,
      detalle_activos
    };
    render();
  },
  "af-descartar-depreciacion": () => {
    if (!confirm("¿Descartar esta depreciación calculada? No se guardará nada.")) return;
    state.afLiquidacionActual = null;
    render();
  },
  "af-confirmar-depreciacion": async () => {
    const liq = state.afLiquidacionActual;
    if (!liq || (liq.detalle_activos || []).length === 0) return;
    try {
      const numero = await siguienteNumeroDoc("DEP");
      const asiento = await generarAsientoDepreciacion({ ...liq, numero });
      const activos = (state.data.activos_fijos || []).map(a => {
        const det = liq.detalle_activos.find(d => d.activoId === a.id);
        if (!det) return a;
        const { activoId, activoNombre, ...record } = det;
        return {
          ...a,
          depreciaciones: [...(a.depreciaciones || []), { ...record, asiento_id: asiento.id, numero: asiento.numero }]
        };
      });
      saveData("activos_fijos", activos);
      state.afLiquidacionActual = null;
      state.afTab = "historico";
      alert(`✅ Depreciación ${numero} confirmada. Asiento contable ${asiento.numero} generado en el Libro Diario.`);
      render();
    } catch (err) {
      alert("⚠️ " + err.message);
    }
  },
  "af-seleccionar-baja": (d) => {
    state.afBajaActivoId = d.id;
    render();
  },
  "af-cancelar-baja": () => {
    state.afBajaActivoId = null;
    render();
  },
  "af-confirmar-baja": async (d) => {
    const activo = (state.data.activos_fijos || []).find(a => a.id === d.id);
    if (!activo) return;
    const fecha_baja = document.getElementById("af-fecha-baja")?.value || today();
    const motivo_baja = document.getElementById("af-motivo-baja")?.value;
    const valor_venta = parseNumber(document.getElementById("af-valor-venta")?.value) || 0;
    const medio = document.getElementById("af-medio-pago-baja")?.value;

    const baja = calcularBajaActivo(activo, valor_venta);
    try {
      const asiento = await generarAsientoBajaActivo(activo, baja, medio);
      const activos = (state.data.activos_fijos || []).map(a => a.id !== activo.id ? a : {
        ...a,
        estado: "dado_de_baja", fecha_baja, motivo_baja, valor_venta,
        depreciaciones: [...(a.depreciaciones || []), {
          fecha: fecha_baja, origen: "baja",
          valorVenta: baja.valorVenta, utilidadPerdida: baja.utilidadPerdida, esUtilidad: baja.esUtilidad,
          asiento_id: asiento.id, numero: asiento.numero
        }]
      });
      saveData("activos_fijos", activos);
      state.afBajaActivoId = null;
      state.afTab = "historico";
      alert(`✅ Baja registrada. Asiento contable ${asiento.numero} generado (${baja.esUtilidad ? "utilidad" : "pérdida"} en venta: ${fmtCOP(Math.abs(baja.utilidadPerdida))}).`);
      render();
    } catch (err) {
      alert("⚠️ " + err.message);
    }
  }
});
